import { useMemo } from "react";
import styles from "../CategorizedNomenclatureListPage.module.scss";
import { IAppAccordionTab } from "../../components/AppAccordion/AppAccordion";
import { IDropdownMenuItem } from "../../components/AppDropdownMenu";
import { CategoryHeader } from "../components/CategoryHeader";
import { CategoryItemsList } from "../components/CategoryItemsList";
import {
  ICategorizableItem,
  INomenclatureCategory,
} from "../models/categorizedNomenclatureModels";

interface UseAccordionTabsParams<
  TItemModel extends ICategorizableItem,
  TCategory extends INomenclatureCategory
> {
  categories: TCategory[];
  itemsByCategory: Record<number | "uncategorized", TItemModel[]>;
  isLoading: boolean;
  emptyListText: string;
  itemTemplate: (
    item: TItemModel | undefined,
    index: number,
    isLoading: boolean,
    actionButtons?: React.ReactNode,
    className?: string,
    onEditItem?: (item: TItemModel) => void
  ) => JSX.Element;
  onItemClick: (item: TItemModel) => void;
  onEditItem: (item: TItemModel) => void;
  getCategoryMenuItems: (categoryId: number, itemCount: number) => IDropdownMenuItem[];
  getUncategorizedMenuItems: (itemCount: number) => IDropdownMenuItem[];
  toggleDeletionEndpoint: string;
  updateEndpoint: string;
  invalidateKeys: (string | number | undefined)[][];
  /** When true, suppresses the uncategorized fallback tab when it has no items. */
  isSearching?: boolean;
  /** Emoji to show next to the uncategorized category header. Default: none. */
  uncategorizedEmoji?: string;
}

interface UseAccordionTabsResult {
  accordionTabs: IAppAccordionTab[];
  activeCategories: INomenclatureCategory[];
  hasUncategorized: boolean;
  hasDeletedItems: boolean;
  allDeletedItems: ICategorizableItem[];
}

export function useAccordionTabs<
  TItemModel extends ICategorizableItem,
  TCategory extends INomenclatureCategory
>({
  categories,
  itemsByCategory,
  isLoading,
  emptyListText,
  itemTemplate,
  onItemClick,
  onEditItem,
  getCategoryMenuItems,
  getUncategorizedMenuItems,
  toggleDeletionEndpoint,
  updateEndpoint,
  invalidateKeys,
  isSearching = false,
  uncategorizedEmoji,
}: UseAccordionTabsParams<TItemModel, TCategory>): UseAccordionTabsResult {
  const activeCategories = useMemo(
    () => categories.filter((c) => !c.isDeleted),
    [categories]
  );

  const uncategorizedItems = useMemo(
    () => itemsByCategory.uncategorized ?? [],
    [itemsByCategory]
  );

  const allDeletedItems = useMemo(() => {
    const deleted: TItemModel[] = [];
    activeCategories.forEach((category) => {
      const categoryItems = itemsByCategory[category.id] ?? [];
      deleted.push(...(categoryItems.filter((i) => i.isDeleted) as TItemModel[]));
    });
    deleted.push(...(uncategorizedItems.filter((i) => i.isDeleted) as TItemModel[]));
    return deleted;
  }, [activeCategories, itemsByCategory, uncategorizedItems]);

  const hasUncategorized =
    uncategorizedItems.filter((i) => !i.isDeleted).length > 0 ||
    (!isSearching && activeCategories.length === 0);
  const hasDeletedItems = allDeletedItems.length > 0;

  const accordionTabs: IAppAccordionTab[] = useMemo(() => {
    const tabs: IAppAccordionTab[] = [];

    // Category tabs
    activeCategories.forEach((category) => {
      const categoryItems = itemsByCategory[category.id] ?? [];
      const activeItems = categoryItems.filter((i) => !i.isDeleted) as TItemModel[];
      const menuItems = getCategoryMenuItems(category.id, activeItems.length);

      tabs.push({
        id: category.id,
        header: (
          <CategoryHeader
            name={category.name}
            itemCount={activeItems.length}
            menuItems={menuItems}
            emoji={(category as any).emoji}
          />
        ),
        content: (
          <CategoryItemsList
            items={activeItems}
            isLoading={isLoading}
            emptyListText={emptyListText}
            itemTemplate={itemTemplate}
            onItemClick={onItemClick}
            onEditItem={onEditItem}
            toggleDeletionEndpoint={toggleDeletionEndpoint}
            updateEndpoint={updateEndpoint}
            invalidateKeys={invalidateKeys}
          />
        ),
      });
    });

    // Uncategorized tab
    if (hasUncategorized) {
      const activeUncategorized = uncategorizedItems.filter(
        (i) => !i.isDeleted
      ) as TItemModel[];
      const menuItems = getUncategorizedMenuItems(activeUncategorized.length);

      tabs.push({
        id: "uncategorized",
        header: (
          <CategoryHeader
            name="Без категория"
            itemCount={activeUncategorized.length}
            menuItems={menuItems}
            emoji={uncategorizedEmoji}
            dataOnboarding="uncategorized-row"
            dataOnboardingMenu="uncategorized-menu"
          />
        ),
        content: (
          <CategoryItemsList
            items={activeUncategorized}
            isLoading={isLoading}
            emptyListText={emptyListText}
            itemTemplate={itemTemplate}
            onItemClick={onItemClick}
            onEditItem={onEditItem}
            toggleDeletionEndpoint={toggleDeletionEndpoint}
            updateEndpoint={updateEndpoint}
            invalidateKeys={invalidateKeys}
          />
        ),
      });
    }

    // Virtual "Deleted" category tab
    if (hasDeletedItems) {
      tabs.push({
        id: "deleted",
        header: (
          <CategoryHeader
            name="Изтрити"
            itemCount={allDeletedItems.length}
            isDeleted
          />
        ),
        content: (
          <CategoryItemsList
            items={allDeletedItems}
            isLoading={isLoading}
            emptyListText="Няма изтрити елементи"
            itemTemplate={itemTemplate}
            toggleDeletionEndpoint={toggleDeletionEndpoint}
            updateEndpoint={updateEndpoint}
            invalidateKeys={invalidateKeys}
            isDeletedCategory
          />
        ),
        headerClassName: styles.deleted_category_header,
      });
    }

    return tabs;
  }, [
    activeCategories,
    allDeletedItems,
    emptyListText,
    getCategoryMenuItems,
    getUncategorizedMenuItems,
    hasDeletedItems,
    hasUncategorized,
    isLoading,
    itemTemplate,
    itemsByCategory,
    invalidateKeys,
    onEditItem,
    onItemClick,
    toggleDeletionEndpoint,
    uncategorizedItems,
    updateEndpoint,
  ]);

  return {
    accordionTabs,
    activeCategories,
    hasUncategorized,
    hasDeletedItems,
    allDeletedItems,
  };
}
